/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { IWssEvent } from "./wss-event.interface";
import { WssSubscriptionFilter } from "./wss-subscription.types";

/**
 * A reference to a resource as it travels over the WebSocket, e.g. `{ refType: "ticket", refId: "..." }`.
 * Structurally identical to the `IReference` entity, kept separate so the transport layer does not depend on models.
 */
export interface IWssReference {
  refType: string;
  refId: string;
}

/**
 * The references an activity carries. Shared by the event payload and the activity log entity,
 * so the predicates below work on both.
 */
export interface IWssActivityReferences {
  /**
   * The resource the activity belongs to, e.g. the ticket that was commented on
   */
  source?: IWssReference;

  /**
   * Resources the activity refers to, e.g. the users mentioned in a comment
   */
  target?: IWssReference[];
}

/**
 * Payload of the events the backend publishes for activity log entries.
 *
 * The event name is `<source.refType>.<actionType>.<activity>`, e.g. "ticket.create.comment" for a comment on a ticket,
 * so `*.create.comment` (see `WssEventNameEnum.ANY_COMMENT_CREATE`) receives every new comment.
 *
 * The payload does not carry the activity itself, only its id: fetch the activity to display it.
 */
export interface IWssActivityEventData extends IWssActivityReferences {
  /**
   * Id of the activity log entry that was created
   */
  activityId: string;

  /**
   * The resource the activity belongs to, e.g. the ticket that was commented on
   */
  source: IWssReference;

  /**
   * The action type, e.g. "create"
   */
  actionType: string;

  /**
   * The activity type, e.g. "comment"
   */
  activity: string;
}

/**
 * The reference type of a user, as used in activity targets (e.g. mentions and assignees).
 */
export const WSS_USER_REF_TYPE = BackendResourceEnum.USER;

/**
 * Whether two references point at the same resource.
 */
export function isSameWssReference(a?: IWssReference | null, b?: IWssReference | null): boolean {
  if (!a?.refId || !b?.refId) {
    return false;
  }

  return a.refId === b.refId && a.refType === b.refType;
}

/**
 * Whether the activity belongs to the given reference: the reference is either the source of the
 * activity (a comment on the ticket) or one of its targets (a comment elsewhere that mentions the ticket).
 *
 * @param activity the payload of the activity event, or the activity log entry itself
 * @param reference the resource whose activity log is displayed
 */
export function isActivityForReference(
  activity: IWssActivityReferences | undefined,
  reference?: IWssReference | null
): boolean {
  if (!activity || !reference?.refId) {
    return false;
  }

  if (isSameWssReference(activity.source, reference)) {
    return true;
  }

  return (activity.target ?? []).some((target) => isSameWssReference(target, reference));
}

/**
 * Whether the activity targets the given user, e.g. the user was mentioned in the comment.
 *
 * @param activity the payload of the activity event, or the activity log entry itself
 * @param userId the id of the user, usually the one that is logged in
 */
export function isActivityTargetingUser(activity: IWssActivityReferences | undefined, userId?: string): boolean {
  if (!activity || !userId) {
    return false;
  }

  return (activity.target ?? []).some((target) => target.refType === WSS_USER_REF_TYPE && target.refId === userId);
}

/**
 * Creates a filter that only passes activity events that belong to the reference returned by `getReference`.
 * The reference is read on every event, so it follows the component when the displayed resource changes.
 *
 * @param getReference returns the resource whose activity log is displayed, or nothing to ignore all events
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.ANY_COMMENT_CREATE,
 *   filter: forActivityReference(() => this.source),
 *   handler: (event) => this.fetchActivity(event.data.activityId)
 * }
 * ```
 */
export function forActivityReference<T extends IWssActivityEventData = IWssActivityEventData>(
  getReference: () => IWssReference | undefined | null
): WssSubscriptionFilter<T> {
  return (event: IWssEvent<T>): boolean => {
    return isActivityForReference(event.data, getReference());
  };
}

/**
 * Creates a filter that only passes activity events that target the user returned by `getUserId`.
 *
 * @param getUserId returns the id of the user, usually the one that is logged in
 *
 * @example
 * ```typescript
 * {
 *   event: WssEventNameEnum.ANY_COMMENT_CREATE,
 *   filter: forTargetUser(() => UserModule.userId),
 *   handler: () => this.$toast.info(this.$t("..."))
 * }
 * ```
 */
export function forTargetUser<T extends IWssActivityEventData = IWssActivityEventData>(
  getUserId: () => string | undefined
): WssSubscriptionFilter<T> {
  return (event: IWssEvent<T>): boolean => {
    return isActivityTargetingUser(event.data, getUserId());
  };
}
